import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Link, Stack, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import routes from 'common/routes';
import StyledAuthCard from 'common/theme/layouts/auth/StyledAuthCard';
import StyledAuthFormZone from 'common/theme/layouts/auth/StyledAuthFormZone';
import StyledAuthRailTab from 'common/theme/layouts/auth/StyledAuthRailTab';

import { ReactComponent as Logotype } from 'images/logotype.svg';
import heroImage from 'images/login.webp';

// Curva y duración del sistema (motion §0).
const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

// Rail izquierdo (md+) / barra superior de tabs (xs). El indicador es un bloque que
// desliza con transform entre las dos tabs.
const railSx = {
  display: 'flex',
  flexDirection: { xs: 'row', md: 'column' },
  alignItems: 'stretch',
  gap: 1,
  flex: { md: '0 0 176px' },
  p: { xs: 1.5, md: 2 },
  borderRight: { md: '1px solid' },
  borderBottom: { xs: '1px solid', md: 0 },
  borderColor: { xs: 'hairline.main', md: 'hairline.main' }
};

// El hero central: la foto que antes era fondo de página, con scrim para que el
// titular blanco lea AA. Banda oscura en AMBOS modos (mismo criterio que brand.hero*).
// La foto PANEA: en "Ingresar" se ve una mitad; al pasar a "Crear cuenta" se
// desliza y se revela la otra mitad (la imagen tiene dos sujetos).
const heroSx = (isSignUp) => ({
  position: 'relative',
  overflow: 'hidden',
  flex: { md: '0 0 340px' },
  minHeight: { xs: 148, md: 'auto' },
  display: 'flex',
  alignItems: 'flex-end',
  backgroundImage: `linear-gradient(180deg, rgba(10, 12, 14, 0.35) 0%, rgba(10, 12, 14, 0.85) 100%), url(${heroImage})`,
  backgroundSize: 'cover',
  // Medido sobre login.webp (3787x2434): la cara de la mujer está al ~53% del ancho y
  // la del hombre al ~81%. "Ingresar" centra al hombre (95%, para que no asome el brazo
  // de la chica) y "Crear cuenta" a la mujer (52%).
  backgroundPosition: isSignUp ? '52% center' : '95% center',
  transition: `background-position 550ms ${EASE}`,
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
});

// Titulares del hero: dos bloques superpuestos que crossfadean según la vista.
const heroCopySx = (visible) => ({
  position: visible ? 'relative' : 'absolute',
  inset: visible ? 'auto' : 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  p: { xs: 2, md: 3 },
  opacity: visible ? 1 : 0,
  transform: visible ? 'none' : 'translateY(12px)',
  transition: `opacity 450ms ${EASE}, transform 450ms ${EASE}`,
  pointerEvents: 'none',
  '@media (prefers-reduced-motion: reduce)': { transition: 'none', transform: 'none' }
});

/**
 * Card único de las pantallas públicas: rail con logo + tabs (Ingresar / Crear
 * cuenta) con indicador deslizante, hero central con la foto y titular animado, y
 * la zona de formularios ({children}). Vive en LoginLayout, así que PERSISTE entre
 * rutas públicas: el rail y el hero animan aunque la pantalla interna remonte.
 */
function AuthCard({ children }) {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isSignUp = pathname === routes.register.path;
  const isPortal = isSignUp || pathname === routes.login.path || pathname === routes.home.path;

  return (
    <StyledAuthCard>
      <Box component="nav" aria-label={t('tabs.ariaLabel', { ns: 'auth', defaultValue: 'Acceso' })} sx={railSx}>
        {/* Logo: arriba en desktop, oculto en xs (el hero compacto ya lo muestra). */}
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            mb: 3,
            px: 1,
            pt: 1,
            '& svg': { width: '100%', height: 'auto', filter: (theme) => (theme.palette.mode === 'light' ? 'brightness(0)' : 'none') }
          }}>
          <Logotype />
        </Box>

        {isPortal ? (
          <Box sx={{ position: 'relative', display: 'flex', flexDirection: { xs: 'row', md: 'column' }, gap: 1, flex: 1 }}>
            {/* Indicador deslizante: gris neutro (selected.bg), sin dorado. */}
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                borderRadius: '13px',
                bgcolor: 'selected.bg',
                width: { xs: 'calc(50% - 4px)', md: '100%' },
                height: { xs: '100%', md: 48 },
                top: 0,
                left: 0,
                transform: {
                  xs: isSignUp ? 'translateX(calc(100% + 8px))' : 'none',
                  md: isSignUp ? 'translateY(56px)' : 'none'
                },
                transition: `transform 350ms ${EASE}`,
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
              }}
            />
            <StyledAuthRailTab active={!isSignUp} onClick={() => navigate(routes.login.path)} sx={{ zIndex: 1, flex: { xs: 1, md: 'none' } }}>
              {t('tabs.signIn', { ns: 'auth', defaultValue: 'Ingresar' })}
            </StyledAuthRailTab>
            <StyledAuthRailTab active={isSignUp} onClick={() => navigate(routes.register.path)} sx={{ zIndex: 1, flex: { xs: 1, md: 'none' } }}>
              {t('tabs.signUp', { ns: 'auth', defaultValue: 'Crear cuenta' })}
            </StyledAuthRailTab>
          </Box>
        ) : (
          <Stack sx={{ flex: 1, justifyContent: { xs: 'center', md: 'flex-start' } }}>
            <Link
              component={RouterLink}
              to={routes.login.path}
              underline="hover"
              sx={{
                alignItems: 'center',
                color: 'text.secondary',
                display: 'inline-flex',
                fontFamily: 'inherit',
                fontSize: '0.8rem',
                fontWeight: 700,
                gap: 0.75,
                justifyContent: { xs: 'center', md: 'flex-start' },
                px: 1
              }}>
              <ArrowBack sx={{ fontSize: 16 }} />
              {t('backToLogin', { ns: 'auth', defaultValue: 'Volver a ingresar' })}
            </Link>
          </Stack>
        )}
      </Box>

      <Box sx={heroSx(isSignUp)} aria-hidden>
        {/* Logo del hero visible solo en xs (en md+ ya está en el rail). */}
        <Box
          sx={{
            position: 'absolute',
            top: { xs: 14, md: 18 },
            left: { xs: 16, md: 24 },
            width: { xs: 110, md: 130 },
            display: { xs: 'block', md: 'none' },
            '& svg': { width: '100%', height: 'auto' }
          }}>
          <Logotype />
        </Box>
        <Box sx={heroCopySx(!isSignUp)}>
          <Typography variant="h5" sx={{ color: '#FFFFFF', fontWeight: 800, letterSpacing: '-0.02em' }}>
            {t('hero.signInTitle', { ns: 'auth', defaultValue: 'Bienvenido de vuelta' })}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.72)' }}>
            {t('hero.signInSubtitle', { ns: 'auth', defaultValue: 'Tu entrenamiento te está esperando.' })}
          </Typography>
        </Box>
        <Box sx={heroCopySx(isSignUp)}>
          <Typography variant="h5" sx={{ color: '#FFFFFF', fontWeight: 800, letterSpacing: '-0.02em' }}>
            {t('hero.signUpTitle', { ns: 'auth', defaultValue: 'Únete al club' })}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.72)' }}>
            {t('hero.signUpSubtitle', { ns: 'auth', defaultValue: 'Crea tu cuenta y empieza hoy.' })}
          </Typography>
        </Box>
      </Box>

      <StyledAuthFormZone>{children}</StyledAuthFormZone>
    </StyledAuthCard>
  );
}

AuthCard.propTypes = {
  children: PropTypes.node.isRequired
};

export default AuthCard;
